/**
 * @file AddEditSkillDialog.jsx
 * @description Add/Edit Skill Dialog component for displaying and managing skills.
 * @component AddEditSkillDialog
 * @exports AddEditSkillDialog - Main component for displaying and managing skills.
 */

import React from "react";
import Grid from "@mui/material/Grid2";
import { Button, TextField, Box, Switch, FormControl, FormControlLabel, InputLabel, Select, MenuItem } from "@mui/material";
import { QUEUE_STRINGS } from "../../../common/constants/ivrModule/queueConstants.js";
import { SKILL_STRINGS, SKILL_KEYS } from "../../../common/constants/adminModule/skillConstants.js";
import { STRINGS, SIZES, VARIANTS, BUTTON_COLORS, AUTO_COMPLETE } from "../../../common/constants/commonConstants.js";
import DialogComponent from "../../../common/components/DialogComponent.jsx";

const AddEditSkillDialog = ({
    ivrQueues,
    onSave,
    selectedSkill,
    open,
    handleInputChange,
    onClose,
}) => {
    return (
        <>
            <DialogComponent
                open={open}
                setWidth="xs"
                onClose={onClose}
                title={selectedSkill?.id ? SKILL_STRINGS.EDIT_SKILL : SKILL_STRINGS.ADD_SKILL}
                actions={
                    <Button
                        onClick={onSave}
                        variant={VARIANTS.CONTAINED}
                        color={BUTTON_COLORS.PRIMARY}
                    >
                        {STRINGS.SAVE}
                    </Button>
                }
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Grid
                        container
                        spacing={2}
                        sx={{
                            alignItems: "center",
                        }}
                    >
                        <Grid item size={SIZES.GROW}>
                            <TextField
                                fullWidth
                                size={SIZES.SMALL}
                                variant={VARIANTS.OUTLINED}
                                label={SKILL_STRINGS.SKILL_NAME}
                                autoComplete={AUTO_COMPLETE.OFF}
                                value={selectedSkill?.skillname || ""}
                                onChange={handleInputChange(SKILL_KEYS.SKILL_NAME)}
                            />
                        </Grid>
                        {selectedSkill?.id && (
                            <>
                                <Grid item size={SIZES.AUTO}>
                                    <Switch
                                        checked={selectedSkill?.inactive === null || selectedSkill?.inactive === 0}
                                        onChange={(e) => handleInputChange(SKILL_KEYS.INACTIVE)({
                                            target: { value: e.target.checked ? 0 : 1 }
                                        })}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item size={8}>
                            <FormControlLabel 
                                labelPlacement="start"
                                label={SKILL_STRINGS.ENABLE_QUEUEID}
                                sx={{ marginLeft: 0, marginRight: 0, justifyContent: "flex-end" }}
                                control={<Switch 
                                    checked={selectedSkill?.queueIdreq === 1}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        handleInputChange(SKILL_KEYS.QUEUE_ID_REQUIRED)({
                                            target: { value: e.target.checked ? 1 : 0 }
                                        });
                                        if (checked && !selectedSkill?.isVoice) {
                                            handleInputChange(SKILL_KEYS.IS_VOICE)({
                                                target: { value: true }
                                            });
                                        }
                                    }}
                                />}
                            />
                        </Grid>
                        {selectedSkill?.queueIdreq === 1 && (
                            <Grid
                                item
                                size={4}
                                display="flex"
                                justifyContent="flex-end"
                                alignItems="center"
                            >
                                <FormControlLabel
                                    labelPlacement="start"
                                    label={SKILL_STRINGS.IS_VOICE}
                                    sx={{ marginLeft: 0, marginRight: 0, justifyContent: "flex-end" }}
                                    control={
                                        <Switch
                                            checked={
                                                selectedSkill?.isVoice === true
                                            }
                                            onChange={(e) =>
                                                handleInputChange(SKILL_KEYS.IS_VOICE)({
                                                    target: { value: e.target.checked ? true : false },
                                                })
                                            }
                                        />
                                    }
                                />
                            </Grid>
                        )}
                    </Grid>
                    {selectedSkill?.queueIdreq === 1 && (
                        <FormControl fullWidth size={SIZES.SMALL}>
                            <InputLabel>{QUEUE_STRINGS.QUEUE_ID}</InputLabel>
                            <Select
                                label={QUEUE_STRINGS.QUEUE_ID}
                                value={selectedSkill?.queueid || ""}
                                onChange={handleInputChange(SKILL_KEYS.QUEUE_ID)}
                            >
                                {ivrQueues.map((ivrQueue) => (
                                    <MenuItem key={ivrQueue.id} value={ivrQueue.id}>
                                        {ivrQueue.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Box>
            </DialogComponent>
        </>
    )
};

export default AddEditSkillDialog;